import { apiRequest, apiRequestPage, type PaginationMeta } from './client';

/**
 * Clients for FEAT-023 (settings), FEAT-022 (integrations) and FEAT-018 /
 * §22.5 (the caller's own calendar connection).
 *
 * One file because one screen shows all three, and because they share a single
 * fact worth stating once: **no response here ever carries a secret.** A
 * sensitive setting comes back as `is_set` and nothing more (BR-082), and the
 * calendar status names the connected account but never the token.
 */

export type SettingType =
  'string' | 'integer' | 'boolean' | 'enum' | 'email' | 'url' | 'timezone' | 'secret' | 'file_ref';

export interface Setting {
  key: string;
  type: SettingType;
  description: string;
  sensitive: boolean;
  /** Always null for a sensitive key — see `is_set`. */
  value: string | number | boolean | null;
  /** Whether a credential exists. One bit; never its length or its content. */
  is_set: boolean;
  /** True while no administrator has overridden the shipped default (BR-080). */
  is_default: boolean;
}

export function listSettings(): Promise<Setting[]> {
  return apiRequest<Setting[]>('/settings');
}

export function setSetting(key: string, value: string): Promise<Setting[]> {
  return apiRequest<Setting[]>(`/settings/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: { value },
  });
}

export function resetSetting(key: string): Promise<Setting[]> {
  return apiRequest<Setting[]>(`/settings/${encodeURIComponent(key)}`, { method: 'DELETE' });
}

export type CalendarProviderId = 'google' | 'microsoft';

export interface CalendarProviderAvailability {
  provider: CalendarProviderId;
  label: string;
  /** False until an administrator has configured a client id and secret. */
  available: boolean;
  reason?: string;
}

export interface CalendarConnection {
  connected: boolean;
  provider: CalendarProviderId | null;
  account_email: string | null;
  connected_at: string | null;
  /** The provider rejected the stored authorisation; the user must reconnect. */
  needs_reconnect: boolean;
  available: CalendarProviderAvailability[];
}

export function calendarStatus(): Promise<CalendarConnection> {
  return apiRequest<CalendarConnection>('/me/calendar');
}

export function beginCalendarConnect(provider: CalendarProviderId): Promise<{ url: string }> {
  return apiRequest<{ url: string }>('/me/calendar/connect', {
    method: 'POST',
    body: { provider },
  });
}

export function disconnectCalendar(): Promise<CalendarConnection> {
  return apiRequest<CalendarConnection>('/me/calendar', { method: 'DELETE' });
}

/**
 * FEAT-018 Stage 6 / ADR 0009 — the account Boud reads *room* calendars with.
 *
 * Distinct from `CalendarConnection` above, which is a person's own calendar.
 * The two look similar and mean different things: one determines where a user's
 * bookings are mirrored, the other determines what the platform can see.
 */
export interface ServiceCalendar {
  provider: CalendarProviderId;
  account_email: string;
  connected_at: string;
  /** The credential lapsed. Inbound sync has stopped; outbound is unaffected. */
  needs_attention: boolean;
}

export function listServiceCalendars(): Promise<ServiceCalendar[]> {
  return apiRequest<ServiceCalendar[]>('/calendar/service');
}

export function connectServiceCalendar(provider: CalendarProviderId): Promise<{ url: string }> {
  return apiRequest<{ url: string }>('/calendar/service/connect', {
    method: 'POST',
    body: { provider },
  });
}

export function disconnectServiceCalendar(provider: CalendarProviderId): Promise<void> {
  return apiRequest<void>(`/calendar/service/${provider}`, { method: 'DELETE' });
}

/** What the inbound worker *would* have done — shadow mode's whole output. */
export interface InboundLogEntry {
  id: string;
  room_id: string;
  room_name: string;
  external_id: string;
  decision: 'own_echo' | 'would_create' | 'would_update' | 'would_cancel' | 'conflict' | 'ignored';
  reason: string | null;
  booking_id: string | null;
  title: string | null;
  starts_at: string | null;
  ends_at: string | null;
  organiser_email: string | null;
  observed_at: string;
}

/**
 * Paged, like the security log.
 *
 * It used to fetch a flat 200 rows — a hard ceiling wearing the clothes of a
 * complete answer, where the 201st entry simply did not exist as far as anybody
 * reading the screen could tell.
 */
export function listInboundLog(query: {
  page: number;
  pageSize: number;
  decision?: string;
}): Promise<{ items: InboundLogEntry[]; meta: PaginationMeta }> {
  return apiRequestPage<InboundLogEntry>('/calendar/inbound-log', {
    query: {
      page: query.page,
      page_size: query.pageSize,
      ...(query.decision ? { decision: query.decision } : {}),
    },
  });
}

/** FEAT-018 Stage 9 — per-room sync health. */
export interface SyncHealth {
  room_id: string;
  room_name: string;
  resource_email: string | null;
  inbound_enabled: boolean;
  last_swept_at: string | null;
  minutes_since_sweep: number | null;
  /** Past the point where silence stops being ordinary. */
  stale: boolean;
  last_error: string | null;
}

export function listSyncHealth(): Promise<SyncHealth[]> {
  return apiRequest<SyncHealth[]>('/calendar/health');
}
